import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressSummary = ({ currentStep, totalSteps, formData }) => {
  const completionPercentage = ((currentStep - 1) / (totalSteps - 1)) * 100;
  
  const getEstimatedTime = () => {
    const remainingSteps = totalSteps - currentStep;
    return remainingSteps * 2;
  };

  const getCompletedFields = () => {
    let completed = 0;
    let total = 0;

    // Step 1 fields
    total += 4;
    if (formData?.username) completed++;
    if (formData?.email) completed++;
    if (formData?.password) completed++;
    if (formData?.confirmPassword) completed++;

    // Step 2 fields
    total += 1;
    if (formData?.guildName) completed++;

    // Step 3 fields
    total += 4;
    if (formData?.characterName) completed++;
    if (formData?.characterClass) completed++;
    if (formData?.characterLevel) completed++;
    if (formData?.server) completed++;

    return { completed, total };
  };

  const { completed, total } = getCompletedFields();

  return (
    <div className="bg-card rounded-xl p-4 md:p-5 lg:p-6 border border-border sticky top-24">
      <div className="flex items-center gap-3 mb-4 md:mb-5">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Icon name="ClipboardList" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground">
            Registration Progress
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Step {currentStep} of {totalSteps}
          </p>
        </div>
      </div>

      <div className="space-y-4 md:space-y-5">
        {/* Overall Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs md:text-sm text-muted-foreground">Overall Progress</span>
            <span className="text-xs md:text-sm font-medium text-foreground">
              {Math.round(completionPercentage)}%
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        {/* Fields Completed */}
        <div className="flex items-center justify-between py-3 border-t border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="CheckSquare" size={16} color="var(--color-success)" />
            <span className="text-sm md:text-base text-foreground">Fields Completed</span>
          </div>
          <span className="text-sm md:text-base font-medium text-foreground">
            {completed}/{total}
          </span>
        </div>

        {/* Estimated Time */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} color="var(--color-accent)" />
            <span className="text-sm md:text-base text-foreground">Est. Time Remaining</span>
          </div>
          <span className="text-sm md:text-base font-medium text-foreground">
            ~{getEstimatedTime()} min
          </span>
        </div>

        {/* Step Status */}
        <div className="space-y-2 pt-3 border-t border-border">
          <p className="text-xs md:text-sm font-medium text-foreground mb-3">Step Status</p>
          
          <div className={`flex items-center gap-2 text-sm ${currentStep > 1 ? 'text-success' : currentStep === 1 ? 'text-accent' : 'text-muted-foreground'}`}>
            <Icon 
              name={currentStep > 1 ? 'CheckCircle' : currentStep === 1 ? 'Circle' : 'Circle'} 
              size={16} 
            />
            <span>Account Details</span>
          </div>

          <div className={`flex items-center gap-2 text-sm ${currentStep > 2 ? 'text-success' : currentStep === 2 ? 'text-accent' : 'text-muted-foreground'}`}>
            <Icon 
              name={currentStep > 2 ? 'CheckCircle' : currentStep === 2 ? 'Circle' : 'Circle'} 
              size={16} 
            />
            <span>Guild Verification</span>
          </div>

          <div className={`flex items-center gap-2 text-sm ${currentStep > 3 ? 'text-success' : currentStep === 3 ? 'text-accent' : 'text-muted-foreground'}`}>
            <Icon 
              name={currentStep > 3 ? 'CheckCircle' : currentStep === 3 ? 'Circle' : 'Circle'} 
              size={16} 
            />
            <span>Character Creation</span>
          </div>
        </div>

        {/* Auto-save Indicator */}
        <div className="bg-success/10 rounded-lg p-3 flex items-center gap-2">
          <Icon name="Save" size={14} color="var(--color-success)" />
          <span className="text-xs md:text-sm text-success">Auto-save enabled</span>
        </div>
      </div>
    </div>
  );
};

export default ProgressSummary;